import { Helmet } from "react-helmet-async";

const SITE = "https://www.clumen.com";
const DEFAULT_OG = `${SITE}/videos/solutions-hero-poster.jpg`;

interface SeoProps {
  title: string;
  description: string;
  /** Path only, starting with a slash. Combined with the canonical host. */
  path: string;
  /** Optional per-page social image URL (absolute or app-relative). */
  ogImage?: string;
  type?: "website" | "article";
  /** When true, emits `noindex, follow` and skips canonical/og. Use on 404 and internal pages. */
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Per-route head: unique title, description, canonical, and self-referencing
 * og:* / twitter:* tags. Sitewide fallbacks live in index.html for non-JS
 * social crawlers.
 *
 * Rules enforced:
 *  - Canonical URL always uses the production host (never a preview subdomain).
 *  - og:image and twitter:image are always absolute URLs.
 */
const Seo = ({
  title,
  description,
  path,
  ogImage,
  type = "website",
  noindex = false,
  jsonLd,
}: SeoProps) => {
  const url = `${SITE}${path}`;
  const rawImage = ogImage ?? DEFAULT_OG;
  // Guarantee absolute URL — accept "/foo.jpg" or already-absolute values.
  const image = rawImage.startsWith("http") ? rawImage : `${SITE}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      {!noindex && <link rel="canonical" href={url} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="codelumen" />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
